import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useModalOutsideClick from '../../hooks/useModalOutsideClick';
import './ViewPlayerPayoutsModal.css';
import { formatNumber } from '../../../../utils/helpers';

const ViewPlayerPayoutsModal = ({
  show,
  onClose,
  players,
  playerPayouts,
  userPlayerNames
}) => {
  // Add the useModalOutsideClick hook to close when clicking outside
  useModalOutsideClick(show, onClose, "view-player-payouts-overlay");

  // Helper function to get ordinal suffix
  const getOrdinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Create payout rows with position and player/payout info
  const payoutRows = useMemo(() => {
    if (!playerPayouts || !players) return [];

    // Create a map of finish positions to player names
    const finishMap = {};
    players.forEach(player => {
      if (player.finish > 0) {
        finishMap[player.finish] = player.name;
      }
    });

    // Create rows for each payout position
    return playerPayouts.map((payout, index) => {
      const position = index + 1;
      const playerName = finishMap[position];
      
      return {
        place: getOrdinalSuffix(position),
        player: playerName || '', // Empty string if no player yet
        payout: payout
      };
    });
  }, [players, playerPayouts]);

  if (!show) return null;

  return (
    <div className="view-player-payouts-overlay">
      <div className="view-player-payouts-dialog">
        <div className="view-player-payouts-header">
          <h3>Player Payouts</h3>
          <button className="view-player-payouts-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="view-player-payouts-content">
          {payoutRows.length > 0 ? (
            <table className="player-payouts-table">
              <thead>
                <tr>
                  <th>PLACE</th>
                  <th>PLAYER</th>
                  <th>PAYOUT</th>
                </tr>
              </thead>
              <tbody>
                {payoutRows.map((row) => (
                  <tr key={row.place}>
                    <td className="place">{row.place}</td>
                    <td className="player">
                      {row.player && userPlayerNames.includes(row.player.toLowerCase()) && '⭐ '}
                      {row.player}
                    </td>
                    <td className="payout">
                      ${formatNumber(row.payout)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-payouts-message">No payout information available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

ViewPlayerPayoutsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      finish: PropTypes.number
    })
  ).isRequired,
  playerPayouts: PropTypes.arrayOf(PropTypes.number).isRequired,
  userPlayerNames: PropTypes.arrayOf(PropTypes.string)
};

ViewPlayerPayoutsModal.defaultProps = {
  userPlayerNames: []
};

export default ViewPlayerPayoutsModal; 